import React from 'react';

import BooleanInput from '../../../inputs/base/BooleanInput';
import CodeMirrorInput from '../../../inputs/base/CodeMirrorInput';
import ColorInput from '../../../inputs/base/ColorInput';
import FontFamilyInput from '../../../inputs/base/FontFamilyInput';
import PropertySliderInput from '../../../inputs/base/PropertySliderInput';
import Select from '../../../inputs/base/SelectInput';
import TextInput from '../../../inputs/base/TextInput';
import ToggleButtonInput from '../../../inputs/base/ToggleButtonInput';
import Border from '../../../inputs/style/Border';
import BorderRadius from '../../../inputs/style/BorderRadius';
import Color from '../../../inputs/style/Color';
import FontSize from '../../../inputs/style/FontSize';
import FontWeight from '../../../inputs/style/FontWeight';
import Height from '../../../inputs/style/Height';
import Padding from '../../../inputs/style/Padding';
import TextAlign from '../../../inputs/style/TextAlign';
import TextTransform from '../../../inputs/style/TextTransform';

type TInputName =
  | 'backgroundColor'
  | 'border'
  | 'borderRadius'
  | 'color'
  | 'fontFamily'
  | 'fontSize'
  | 'fontWeight'
  | 'height'
  | 'padding'
  | 'textAlign'
  | 'textTransform';

type TPropInput =
  | { type: 'boolean'; props: Parameters<typeof BooleanInput>[0] }
  | { type: 'code'; props: Parameters<typeof CodeMirrorInput>[0] }
  | { type: 'color'; props: Parameters<typeof ColorInput>[0] }
  | { type: 'font-family'; props: Parameters<typeof FontFamilyInput>[0] }
  | { type: 'select'; props: Parameters<typeof Select>[0] }
  | { type: 'text'; props: Parameters<typeof TextInput>[0] }
  | { type: 'slider'; props: Parameters<typeof PropertySliderInput>[0] }
  | { type: 'toolbar-toggle'; props: Parameters<typeof ToggleButtonInput>[0] };

type TProps = {
  name: string;
  input: TInputName | TPropInput | ((props: { name: string }) => JSX.Element);
};

export default function BlockInput({ name, input }: TProps) {
  if (typeof input === 'function') {
    const Component = input;
    return <Component name={name} />;
  }

  switch (input) {
    case 'border':
      return <Border label="Border" name={`${name}.style.border`} />;
    case 'borderRadius':
      return <BorderRadius label="Border radius" name={`${name}.style.borderRadius`} />;
    case 'backgroundColor':
      return <Color label="Background color" name={`${name}.style.backgroundColor`} />;
    case 'color':
      return <Color label="Text color" name={`${name}.style.color`} />;
    case 'fontFamily':
      return <FontFamilyInput label="Font family" name={`${name}.style.fontFamily`} />;
    case 'fontSize':
      return <FontSize label="Font size" name={`${name}.style.fontSize`} />;
    case 'fontWeight':
      return <FontWeight label="Font weight" name={`${name}.style.fontWeight`} />;
    case 'height':
      return <Height label="Height" name={`${name}.style.height`} />;
    case 'padding':
      return <Padding label="Padding" name={`${name}.style.padding`} />;
    case 'textAlign':
      return <TextAlign label="Alignment" name={`${name}.style.textAlign`} />;
    case 'textTransform':
      return <TextTransform label="Text transform" name={`${name}.style.textTransform`} />;
  }

  switch (input.type) {
    case 'boolean':
      return <BooleanInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'code':
      return <CodeMirrorInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'color':
      return <ColorInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'font-family':
      return <FontFamilyInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'select':
      return <Select {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'text':
      return <TextInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'toolbar-toggle':
      return <ToggleButtonInput {...input.props} name={`${name}.props.${input.props.name}`} />;
    case 'slider':
      return <PropertySliderInput {...input.props} name={`${name}.props.${input.props.name}`} />;
  }
}
